import { Home, FileText, Beaker, Calendar, LogOut, Container, MoreVertical, Settings, UsersRound, BookOpen, Sun, Moon, Monitor, FileStack, GitBranch, ClipboardList, Wine, Contact, LayoutDashboard, UserCheck } from "lucide-react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuBadge,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { useClientsMissingDropboxFolders } from "@/hooks/useClients";
import { useDocumentFacilities } from "@/hooks/useFacilities";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useUserProfile, useIsAdmin } from "@/hooks/useUserProfile";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";
import { ReportBugButton } from "@/components/bugs/ReportBugButton";

// Navigation organized by functional groups
const navigationGroups = [
  {
    label: null, // No label for dashboard - it's standalone
    items: [
      { title: "Apps", url: "/apps", icon: LayoutDashboard },
      { title: "Dashboard", url: "/", icon: Home },
    ],
  },
  {
    label: "Operations",
    items: [
      { title: "Calendar", url: "/calendar", icon: Calendar },
      { title: "Tank Planning", url: "/vessels?tab=planning", icon: GitBranch },
      { title: "Documents", url: "/documents", icon: FileStack },
    ],
  },
  {
    label: "Inventory",
    items: [
      { title: "Vessels", url: "/vessels?tab=vessels", icon: Container },
    ],
  },
  {
    label: "Configuration",
    items: [
      { title: "Customers", url: "/customers", icon: UserCheck },
      { title: "Contacts", url: "/contacts", icon: Contact },
      { title: "Settings", url: "/settings", icon: Settings },
    ],
    // User Management added conditionally in render based on admin role
  },
];

// Admin-only nav item
const adminNavItem = { title: "User Management", url: "/users", icon: UsersRound };

export function AppSidebar() {
  const { open, isMobile, setOpenMobile } = useSidebar();
  const { user, signOut } = useAuth();
  const { data: profile } = useUserProfile();
  const isAdmin = useIsAdmin();
  const { data: documentFacilities = [] } = useDocumentFacilities();
  const { data: missingByFacility } = useClientsMissingDropboxFolders(documentFacilities.map((f) => f.id));
  // Distinct clients missing a folder at any facility (not a per-facility sum, so a client
  // missing at both GWH and RP is one alert, not two).
  const dropboxAlertClientIds = new Set(
    [...(missingByFacility?.values() ?? [])].flatMap((m) => m.clients.map((c) => c.id))
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const [isDarkMode, setIsDarkMode] = useState(false);

  const userInitials = user?.email
    ? user.email.substring(0, 2).toUpperCase()
    : "U";

  // Determine the actual applied theme (handle "system" preference)
  useEffect(() => {
    const updateDarkMode = () => {
      if (theme === "dark") {
        setIsDarkMode(true);
      } else if (theme === "light") {
        setIsDarkMode(false);
      } else {
        // theme === "system"
        setIsDarkMode(window.matchMedia("(prefers-color-scheme: dark)").matches);
      }
    };

    updateDarkMode();

    // Listen for system theme changes when in system mode
    if (theme === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => updateDarkMode();
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [theme]);

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className={`border-b border-sidebar-border transition-all duration-300 ${open ? 'p-4' : 'p-2'}`}>
        <div className="flex items-center justify-center relative h-8">
          <img
            src={isDarkMode ? '/gravity-assist-white-logo.svg' : '/gravity-assist-logo.svg'}
            alt="Gravity Assist"
            className={`h-6 absolute left-0 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          />
          <img
            src="/favicon.svg"
            alt="Gravity Assist"
            className={`h-8 w-8 object-contain absolute left-1/2 -translate-x-1/2 transition-opacity duration-300 ${open ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
          />
        </div>
      </SidebarHeader>

      <SidebarContent>
        {navigationGroups.map((group, groupIndex) => {
          // For Configuration group, conditionally add User Management for admins
          const items = group.label === "Configuration" && isAdmin
            ? [...group.items, adminNavItem]
            : group.items;

          return (
            <SidebarGroup key={group.label || 'main'} className={groupIndex === 0 ? '' : 'pt-2'}>
              {group.label && (
                <SidebarGroupLabel className="text-xs font-medium text-sidebar-foreground/50 uppercase tracking-wider px-3 py-2">
                  {group.label}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {items.map((item) => {
                    // Show badge for Clients & Lots only if there are clients missing Dropbox folder (count > 0)
                    const dropboxAlertCount = dropboxAlertClientIds.size;
                    const showBadge = item.title === "Clients & Lots" && dropboxAlertCount > 0;

                    // Determine active state: exact match for "/", pathname+tab for items
                    // with a ?tab= param (so Tank Planning and Vessels don't both highlight).
                    const [itemPath, itemQuery] = item.url.split("?");
                    const itemTab = itemQuery ? new URLSearchParams(itemQuery).get("tab") : null;
                    const currentTab = new URLSearchParams(location.search).get("tab");
                    const isItemActive = itemPath === "/"
                      ? location.pathname === "/"
                      : location.pathname.startsWith(itemPath) &&
                        (itemTab === null || currentTab === itemTab);

                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild isActive={isItemActive}>
                          <NavLink
                            to={item.url}
                            onClick={handleNavClick}
                          >
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </NavLink>
                        </SidebarMenuButton>
                        {showBadge && (
                          <SidebarMenuBadge className="bg-amber-500 text-white">
                            {dropboxAlertCount}
                          </SidebarMenuBadge>
                        )}
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-4">
        <ReportBugButton />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={`w-full hover:bg-transparent ${open ? 'justify-between px-2' : 'justify-center px-0'}`}
            >
              {open ? (
                <>
                  <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarImage src={profile?.avatar_url || ""} />
                      <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start min-w-0 flex-1 overflow-hidden">
                      <span className="text-sm font-medium text-sidebar-foreground truncate w-full text-left">
                        {profile?.display_name || user?.email?.split('@')[0] || 'User'}
                      </span>
                    </div>
                  </div>
                  <MoreVertical className="h-4 w-4 text-sidebar-foreground/70" />
                </>
              ) : (
                <Avatar className="h-8 w-8">
                  <AvatarImage src={profile?.avatar_url || ""} />
                  <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => navigate('/docs')}>
              <BookOpen className="h-4 w-4 mr-2" />
              Documentation
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                {theme === 'dark' ? <Moon className="h-4 w-4 mr-2" /> : theme === 'light' ? <Sun className="h-4 w-4 mr-2" /> : <Monitor className="h-4 w-4 mr-2" />}
                Theme
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup value={theme} onValueChange={setTheme}>
                  <DropdownMenuRadioItem value="light">
                    <Sun className="h-4 w-4 mr-2" />
                    Light
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="dark">
                    <Moon className="h-4 w-4 mr-2" />
                    Dark
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="system">
                    <Monitor className="h-4 w-4 mr-2" />
                    System
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut()} className="text-destructive focus:text-destructive">
              <LogOut className="h-4 w-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
